<template>
  <div>
    <router-view />
    <GdprBanner />
    <q-ajax-bar
      ref="bar"
      position="top"
      color="primary"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useMeta } from 'quasar'
import GdprUiControl from 'src/services/gdpr'
import GdprBanner from 'src/components/gdpr/GdprBanner.vue'

import { capturePosthogEvent } from 'src/plugins/posthog-events'
import { identifyPosthogUser } from 'src/plugins/posthog-identity'
import { useIsAuthenticated, useMsal } from 'services/auth'
import { findCustomPageMeta } from 'router/custom-page-meta'

useMeta({
  // Set default title (will be overwritten by lower levels)
  title: findCustomPageMeta(location.hostname).title
})

const { account } = useMsal()
const isAuthenticated = useIsAuthenticated()

onMounted(() => {
  // Product analytics are captured by PostHog (see src/plugins/posthog.ts),
  // which enriches every $pageview with user and environment context.
  GdprUiControl.load({
    usesStatistics: true,
    usesMarketing: false
  })
})

// Report sign-in to PostHog (replaces the former App Insights
// `UserAuthenticated` event). Fires on load when already signed in and on
// every subsequent sign-in.
//
// The event carries the PostHog distinct id only. The email address is not
// permitted as a PostHog user id and is not sent as an event property either —
// `identifyPosthogUser()` identifies by the B2C `sub` claim and returns it, so
// the id is attached exactly once, from the same resolution that identified
// the user. Internal-vs-external segmentation comes from the `auth_user_type`
// super property registered on every navigation (see src/plugins/posthog.ts).
const onUserAuth = () => {
  if (!account.value) return
  const userId = identifyPosthogUser()
  capturePosthogEvent('UserAuthenticated', userId ? { user_id: userId } : undefined)
}

watch(
  isAuthenticated,
  (val) => {
    if (val && account.value) onUserAuth()
  },
  { immediate: true }
)
</script>
